import mixpanel from 'mixpanel-browser';
import useApi from 'hooks/useApi';
import { BootResponse, OfferData } from 'constants/apiResponses.types';
import LogoutModal from 'components/LogoutModal/LogoutModal';
import {
  CustomBackToGameButton,
  MessageBox,
  ProductData,
  ProductsCollectAnimation,
  PointsCollectAnimation,
  StoreLayout,
  useIsDesktop,
  HeaderWrapper,
  useIsLandscape,
  FooterTabs,
} from '@appcharge/shared-ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ECollectType,
  EEventsType,
  EFeatureFlag,
  ELocalStorageKeys,
  ELoginEvents,
  ELoginEventsV2,
  EOrderStatus,
  EPopupSubType,
  EPublisherWebhookEventType,
  EPublisherWebhookEventTypeV2,
  EPublisherWebhookOfferEventType,
  EPublisherWebhookOfferEventTypeV2,
  EQueryKeys,
  EQueryParams,
  ESessionStorageKeys,
  ESSEventType,
  EStorePhase,
  EAddToHomeScreenButtonPosition,
  EPosition,
} from 'constants/enums';
import {
  extractOfferMainDetails,
  extractOfferMainDetailsV2,
  extractProgressBarMainDetails,
  extractRollingOfferMainDetails,
  extractRollingOfferMainDetailsV2,
  getBadgeName,
  getPlatformData,
  getSelectedProduct,
} from 'utils';
import { webStorageUtil } from 'state/webStorage.state.service';
import useUrlQuery from 'hooks/useUrlQuery';
import './style.scss';
import useCustomEvents from 'hooks/useCustomEvents';
import { useSaveToHomePage } from 'hooks/useSaveToHomePage';
import { AddToHomePage } from 'components/AddToHomePage/AddToHomePage';
import { useLocalizationState } from 'state/hooks/localization.state.hook';
import { useAppState } from 'state/hooks/app.state.hook';
import {
  CreateOrderPayload,
  IProductResponse,
  OfferDataForPublisherEvent,
  FreePrizesData,
} from 'constants/apiRequest.types';
import { Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { usePopupState } from 'state/hooks/popup.state.hook';
import useLoginEvents from 'hooks/useLoginEvents';
import { useLoadImages } from './hooks/useLoadImages.hook';
import animationData from 'assets/animations/confetti-animation.json';
import Lottie from 'lottie-react';
import HashValidationErrorModal from 'components/HashValidationErrorModal/HashValidationErrorModal';
import { IntegrationProfilePicker } from '../login/Components/IntegrationProfilePicker';
import { useSSE } from 'hooks/useSSE';
import { useOffers } from './hooks/useOffers.hook';
import { useTranslation } from 'react-i18next';
import { useEvent } from 'hooks/useEvent';
import { useEventBatchContext } from 'hooks/useEventBatch';
import { Z_INDEX } from 'constants/zIndex';
import { useQueryClient } from 'react-query';
import 'slick-carousel/slick/slick.scss';
import 'slick-carousel/slick/slick-theme.scss';
import { useLoginState } from 'state/hooks/login.state.hook';
import { isMobile } from 'react-device-detect';
import { CheckoutWrapper } from 'components/ CheckoutWrapper/CheckoutWrapper';
import useCoralogixMonitoring from 'hooks/useCoralogixMonitoring';
import { PopupFailScreen } from 'components/PopupFailScreen/PopupFailScreen';

import { useCheckoutMessageHandler } from 'hooks/useCheckoutMessages';

const OFFERS_INTERVAL_TIME = 30000;
const POLLING_INTERVAL = 10000;

interface IOfferBalance {
  images: {
    product: string;
    productPrefix: string;
  };
  product: ProductData;
  formattedQuantity: string;
}

export const ShopPage: React.FC = () => {
  //store config states:
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  //order states:
  const [activeOrderId, setActiveOrderId] = useState<string>('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  //offer states:
  const [shouldReportAndResetOffers, setShouldReportAndResetOffers] = useState<boolean>(true);

  const navigate = useNavigate();
  const [isSSE, setIsSSE] = useState(false);
  const API = useApi({ orderId: activeOrderId, isSSE: isSSE || false });
  const { sendPublisherWebhookLoginEvent, sendPublisherWebhookLoginEventV2 } = useLoginEvents();
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const from = useUrlQuery(EQueryParams.FROM);
  const campaignToken = useUrlQuery(EQueryParams.TOKEN);
  const [campaignOfferId, setCampaignOfferId] = useState<{ offerId: string }>();
  const CAMPAIGN_SOURCE = 'campaign';
  const [loadingFromCampaign, setLoadingFromCampaign] = useState(from === CAMPAIGN_SOURCE);
  const customEvents = useCustomEvents();
  const { logout } = useLoginState();

  const fetchInterval = useRef(0);
  const hasLoggedOffersRenderTime = useRef(false);
  const { hasNoOffers } = useOffers();
  const { t } = useTranslation();
  const { openTooltipEventHandler } = useEvent();
  const { sendEvent, resetObservedOffers } = useEventBatchContext();

  const isDesktop = useIsDesktop();
  const isLandscape = useIsLandscape();
  const { isDisplayAddToHomePageButton } = useSaveToHomePage();
  const { currentLanguage } = useLocalizationState();
  const { allImagesLoaded } = useLoadImages();
  const appState = useAppState();
  const {
    setDisplayLoader,
    selectedOffer,
    setSelectedOffer,
    showConfetti,
    setShowConfetti,
    setFailedHashValidation,
    failedHashValidation,
    setBackgroundProps,
    utms,
    collectedProductsImages,
    setCollectedProductsImages,
    collectedPointsImages,
    setCollectedPointsImages,
    shouldDisplayProductsAnimation,
    isProfileEnabled,
    setIsProfileEnabled,
    setIsTabFooterDisplayed,
    isTabFooterDisplayed,
  } = appState;
  const popupState = usePopupState();
  const { currentPopupDisplayed, setCurrentPopupDisplayed, popupIndexToCollect } = popupState;
  const { logErrorToCoralogix, startTimeMeasure, endTimeMeasure } = useCoralogixMonitoring();
  const [campaignOfferData, setCampaignOfferData] = useState<
    OfferDataForPublisherEvent | undefined
  >(undefined);
  const [stubOffer, setStubOffer] = useState(false);

  const [searchParams] = useSearchParams();

  const utmSource = useMemo(() => utms?.utmSource, [utms?.utmSource]);

  const queryClient = useQueryClient();

  const addToHomeScreenButtonPosition = publisherMetaData?.featureFlags[
    EFeatureFlag.STORE_REPOSITION_ADD_TO_HOME_SCREEN_BUTTON
  ]
    ? EAddToHomeScreenButtonPosition.HEADER
    : EAddToHomeScreenButtonPosition.FLOATING;

  const backgroundImage = useMemo(() => {
    if (!publisherMetaData?.storeTheme) return null;
    const pisTheme = API.getOffers.data?.storeTheme;
    const publisherTheme = publisherMetaData.storeTheme.general;
    return isDesktop || isLandscape
      ? pisTheme?.bgImageDesktop || publisherTheme.backgroundImageDesktop
      : pisTheme?.bgImageMobile || publisherTheme.backgroundImageMobile;
  }, [
    publisherMetaData?.storeTheme,
    API.getOffers.data?.storeTheme?.bgImageDesktop,
    API.getOffers.data?.storeTheme?.bgImageMobile,
    publisherMetaData?.storeTheme?.general?.backgroundImageDesktop,
    publisherMetaData?.storeTheme?.general?.backgroundImageMobile,
    isDesktop,
    isLandscape,
  ]);

  useEffect(() => {
    if (!publisherMetaData?.storeTheme) return;
    setBackgroundProps({
      height: '',
      minHeight: '100vh',
    });

    if (backgroundImage) {
      document.body.style.setProperty('--background-image', `url(${backgroundImage})`);
      document.body.style.setProperty('z-index', `-${Z_INDEX.BASE}`);
      document.body.style.setProperty('position', 'relative');
    }

    return () => {
      setBackgroundProps({
        height: '',
        minHeight: '',
      });
      document.body.style.removeProperty('--background-image');
      document.body.style.removeProperty('z-index');
      document.body.style.removeProperty('position');
    };
  }, [publisherMetaData?.storeTheme, backgroundImage]);

  useEffect(() => {
    window.addEventListener('popstate', () => {
      if ((window as any).Helpshift) {
        (window as any).Helpshift('close');
      }
    });

    if ((window as any).Helpshift) {
      (window as any).Helpshift('close');
      (window as any).Helpshift('show');
    }
  }, []);

  useEffect(() => {
    if (publisherMetaData.supportConfiguration?.postLoginSnippet) {
      const script = document.createElement('script');
      script.src = publisherMetaData.supportConfiguration?.postLoginSnippet;
      document.head.appendChild(script);
      const interval = setInterval(() => {
        const iframe = document.getElementById('hs-web-sdk-iframe-launcher');
        const iframeDoc =
          (iframe as HTMLIFrameElement)?.contentDocument ||
          (iframe as HTMLIFrameElement)?.contentWindow?.document;
        if (iframeDoc) {
          iframeDoc.querySelector('button')?.addEventListener('click', () => {
            const url = new URL(window.location.href);
            url.searchParams.set('support', 'true');
            window.history.pushState({}, '', url);
          });
          clearInterval(interval);
        }
      }, 1000);
    }
  }, []);

  const reportOffersLoaded = () => {
    const tabs = API.getOffers.data?.tabs;
    const offersExtractedData = [
      //TODO: remove after events v1 deprecation
      ...extractOfferMainDetails(API.getOffers.data.offers.promotions, tabs),
      ...extractOfferMainDetails(API.getOffers.data.offers.bundles, tabs),
      ...extractOfferMainDetails(API.getOffers.data.offers.popups, tabs),
      ...extractRollingOfferMainDetails(API.getOffers.data.offers.rolling, tabs),
    ];

    const offersExtractedDataV2 = [
      ...extractOfferMainDetailsV2(API.getOffers.data.offers.promotions, tabs),
      ...extractOfferMainDetailsV2(API.getOffers.data.offers.bundles, tabs),
      ...extractOfferMainDetailsV2(API.getOffers.data.offers.popups, tabs),
      ...extractRollingOfferMainDetailsV2(API.getOffers.data.offers.rolling, tabs),
    ];

    const progressBars = API.getOffers.data.offers.progressBars;
    const progressBarsData = extractProgressBarMainDetails(progressBars);

    customEvents.sendCustomEvent(
      EEventsType.OFFERS_LOADED,
      {
        specialOffers: API.getOffers.data.offers.promotions?.length,
        bundles: API.getOffers.data.offers.bundles?.length,
        popups: API.getOffers.data.offers.popups?.length,
        progressBars: progressBars?.length,
        offersData: offersExtractedData,
        progressBarsData,
      },
      EStorePhase.POST_LOGIN
    );

    API.sendPublisherWebhookEvent.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.OFFERS_LOADED,
        offers: offersExtractedData,
        ...customEvents.getEventsSharedProps(),
      },
    });
    API.sendPublisherWebhookEventV2.mutate({
      eventType: EPublisherWebhookEventTypeV2.OFFERS,
      data: {
        eventName: EPublisherWebhookOfferEventTypeV2.WEBSTORE_OFFERS_LOADED,
        offers: offersExtractedDataV2,
        progressBars: progressBarsData,
        ...customEvents.getEventsSharedProps(),
      },
    });
  };

  const reportAndReset = () => {
    reportOffersLoaded();
    resetObservedOffers();

    setShouldReportAndResetOffers(false);
  };

  useEffect(() => {
    if (!API.getOffers.data?.offers || API.getOffers.isFetching) return;

    if (shouldReportAndResetOffers) {
      reportAndReset();
    }
  }, [API.getOffers.data, API.getOffers.isFetching]);

  useEffect(() => {
    if (API.getOffers.isError) {
      customEvents.resetSession();
      logout();
    }
  }, [API.getOffers.isError]);

  useEffect(() => {
    document.getElementById('root')?.classList.remove('screen-lock');
  }, []);

  useEffect(() => {
    const handleFocus = () => keepAlive();
    const handleBlur = () => clearTimeout(fetchInterval.current);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isDesktop, isLandscape, publisherMetaData]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const init = async () => {
      const device_orientation = window.matchMedia('(orientation: portrait)').matches
        ? 'portrait'
        : 'landscape';

      customEvents.sendCustomEvent(
        EEventsType.SHOP_LAND,
        {
          platform: getPlatformData(),
          source: webStorageUtil.get(ESessionStorageKeys.SOURCE),
          add_to_home_screen_appear: isDisplayAddToHomePageButton,
          device_orientation,
        },
        EStorePhase.POST_LOGIN
      );
      sendPublisherWebhookLoginEvent({
        eventName: ELoginEvents.SHOP_LAND,
        options: {
          playerId: webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA).playerId,
          device_orientation,
        },
      });
      sendPublisherWebhookLoginEventV2({
        eventName: ELoginEventsV2.WEBSTORE_STORE_DISPLAYED,
        eventType: EPublisherWebhookEventTypeV2.POST_LOGIN,
        options: {
          playerId: webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA).playerId,
        },
      });
      API.sendShopLand.mutate();
      await API.getPostLoginData.refetch();
    };
    init();

    try {
      mixpanel.time_event(EEventsType.SHOP_SESSION_DURATION);
    } catch (e) {
      // Mixpanel may not be initialized
    }

    const sendSessionDuration = () => {
      customEvents.sendCustomEvent(
        EEventsType.SHOP_SESSION_DURATION,
        {
          platform: getPlatformData(),
        },
        EStorePhase.POST_LOGIN
      );
    };

    const handleBeforeUnload = () => {
      sendSessionDuration();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendSessionDuration();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sendSessionDuration();
      clearTimeout(fetchInterval.current);
    };
    // eslint-disable-next-line
  }, [isLoggedIn]);

  const keepAlive = async () => {
    if (!webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN)) {
      return logout(utmSource || undefined);
    }

    API.keepAlive.mutate(undefined, {
      onError: () => {
        customEvents.sendCustomEvent(
          EEventsType.LOGOUT,
          {
            reason: 'session ended',
          },
          EStorePhase.PRE_LOGIN
        );
        customEvents.resetSession();
        logout();
      },
      onSuccess: () => {
        customEvents.setDistinctId();
        fetchInterval.current = Number(setTimeout(fetchIntervalHandler, OFFERS_INTERVAL_TIME));
        if (!isLoggedIn) setIsLoggedIn(true);
      },
    });
  };

  useEffect(() => {
    if (API.keepAlive.isIdle) {
      keepAlive();
    }
  }, [API.keepAlive.isIdle]);

  const openLogoutModal = () => {
    customEvents.sendCustomEvent(EEventsType.PROFILE_MENU_CLICKED, {}, EStorePhase.POST_LOGIN);
    setShowLogoutModal(true);
  };

  const fetchIntervalHandler = () => {
    if (!document.hasFocus()) return;
    fetchInterval.current = Number(setTimeout(fetchIntervalHandler, OFFERS_INTERVAL_TIME));
  };

  useEffect(() => {
    if (!publisherMetaData.featureFlags?.store_refresh_offers_stale_mode) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        API.getOffers.refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [publisherMetaData.featureFlags]);

  useEffect(() => {
    const loadingState = !allImagesLoaded || loadingFromCampaign;
    setDisplayLoader(loadingState);
  }, [allImagesLoaded, loadingFromCampaign]);

  // Log offers render time to RUM when offers are fully loaded and rendered
  useEffect(() => {
    if (allImagesLoaded && API.getOffers.data?.offers && !hasLoggedOffersRenderTime.current) {
      hasLoggedOffersRenderTime.current = true;
      endTimeMeasure('offers_render');
    }
  }, [allImagesLoaded, API.getOffers.data?.offers]);

  useEffect(() => {
    const isEnabled = Boolean(
      publisherMetaData?.integration?.isProfilesEnabled &&
        publisherMetaData?.featureFlags[EFeatureFlag.INTEGRATION_PROFILES]
    );
    setIsProfileEnabled(isEnabled);
    if (!isEnabled) webStorageUtil.set(ELocalStorageKeys.INTEGRATION_PORFILE, null);
  }, [
    publisherMetaData.integration.isProfilesEnabled,
    publisherMetaData.featureFlags[EFeatureFlag.INTEGRATION_PROFILES],
  ]);

  const onCollect = async (offerData: OfferData): Promise<void> => {
    const freeOrderData: Partial<CreateOrderPayload> = {
      clientGaId: localStorage.getItem('ga_client_id') || '2134654321',
      paymentProvider: 'free',
      paymentMethod: 'free',
      utmSource: null,
      offerId: offerData.offerId,
      offerJwt: offerData.offerJwt,
      sequence: offerData.indexToCollect,
    };
    try {
      customEvents.sendCustomEvent(
        EEventsType.COLLECT_CLICKED,
        {
          offer_id: offerData.offerId,
          offer_name: offerData.offerName,
          platform: getPlatformData(),
          type: ECollectType.OFFER,
        },
        EStorePhase.POST_LOGIN
      );

      const response = await API.createOrder.mutateAsync(freeOrderData);
      const orderId = response?.data?.orderId;

      if (orderId) {
        setActiveOrderId(orderId);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('order creating failed', axiosError);
      if (axiosError.response?.status === 410) {
        setFailedHashValidation(true);
        setDisplayLoader(false);
        return;
      }
      customEvents.sendCustomEvent(
        EEventsType.COLLECT_RESOLVED,
        {
          offer_Id: offerData.offerId,
          offer_name: offerData.offerName,
          status: EOrderStatus.REQUEST_FAILED,
          platform: getPlatformData(),
          type: offerData.offerType,
          reason: axiosError.response?.statusText,
          statusCode: axiosError.response?.status,
        },
        EStorePhase.POST_LOGIN
      );
      navigate('../failed?msg=creating order error');
    }
  };

  const currencyCode = useMemo(() => {
    if (!selectedOffer || typeof selectedOffer === 'string') return;

    const selectedProduct = getSelectedProduct(
      selectedOffer as OfferData,
      popupIndexToCollect || 0
    );

    return selectedProduct?.prices?.currencyCode;
  }, [selectedOffer]);

  const selectOffer = (offerData: OfferData) => {
    setDisplayLoader(true);
    setSelectedOffer(offerData);

    const selectedProduct = getSelectedProduct(offerData, popupIndexToCollect || 0);

    const priceData = selectedProduct?.prices;

    if (priceData?.priceInUsdCents === 0) {
      webStorageUtil.set(ELocalStorageKeys.IS_FREE_ORDER_SELECTED, true);
      onCollect(offerData);
    } else {
      webStorageUtil.remove(ELocalStorageKeys.IS_FREE_ORDER_SELECTED);
      setShowPaymentModal(true);
      const products = selectedProduct?.products.map((product) => {
        const { name, quantity } = product;
        return {
          name,
          quantity,
        };
      });
      customEvents.sendCustomEvent(
        EEventsType.OFFER_CLICKED,
        {
          offer_id: offerData.offerId,
          offer_name: offerData.offerName,
          badge_left: getBadgeName('left', offerData?.badges),
          badge_right: getBadgeName('right', offerData?.badges),
          badge_center: getBadgeName('center', offerData?.badges),
          sale_percentage: offerData.productSale?.sale,
          type: offerData.offerType,
          sub_type: offerData?.subType,
          index_to_collect: offerData?.indexToCollect,
          products,
          bundle_price_usd: priceData.priceInUsdCents,
          player_local_currency: priceData.currencyCode,
          bundle_price_total: priceData.priceInUsdCents,
          offer_id_external: offerData.publisherOfferId,
          offer_internal_id: offerData.offerId,
        },
        EStorePhase.POST_LOGIN
      );
      API.sendPublisherWebhookEvent.mutate({
        eventType: EPublisherWebhookEventType.OFFER,
        data: {
          eventName: EPublisherWebhookOfferEventType.OFFER_CLICKED,
          offers: extractOfferMainDetails([offerData]),
          ...customEvents.getEventsSharedProps(),
        },
      });
    }
  };

  const closePopup = () => {
    setDisplayLoader(false);
    setShowLogoutModal(false);
    setShowPaymentModal(false);
    setActiveOrderId('');
    setCurrentPopupDisplayed(null);
    setSelectedOffer(null);
    setLoadingFromCampaign(false);
    setCampaignOfferId(undefined);
    setCampaignOfferData(undefined);
  };

  const getBalancesData = () => {
    const data: IOfferBalance[] = API.getOffers?.data?.balances;
    if (!data) return;
    return data.map((item) => ({
      ...item,
      image: item.images.productPrefix || item.images.product,
      formattedAmount: item.formattedQuantity,
    }));
  };

  useCheckoutMessageHandler({
    navigate,
    setActiveOrderId,
  });

  useEffect(() => {
    // Start timing for offers render measurement
    startTimeMeasure('offers_render');
    hasLoggedOffersRenderTime.current = false;
    API.getOffers.refetch();
  }, [currentLanguage]);

  useEffect(() => {
    if (from === CAMPAIGN_SOURCE && campaignToken) {
      API.getStoreCampaign.mutate(
        {
          tokenId: campaignToken,
        },
        {
          onSuccess: (data: any) => {
            setCampaignOfferId(data);
          },
        }
      );
    }
  }, [from, campaignToken]);

  useEffect(() => {
    if (
      from !== CAMPAIGN_SOURCE ||
      !campaignOfferId ||
      API.getOffers?.isLoading ||
      !API.getOffers.data?.offers
    )
      return;

    const currentCampaignOffer = API.getOffers?.data?.offers?.promotions?.find(
      (promotion: OfferData) => promotion.offerId === campaignOfferId.offerId
    );

    if (currentCampaignOffer) {
      setCampaignOfferData(extractOfferMainDetails([currentCampaignOffer])[0]);
    } else if (from !== CAMPAIGN_SOURCE) {
      setStubOffer(true);
    } else if (typeof campaignOfferId.offerId === 'string') {
      setSelectedOffer(campaignOfferId.offerId);
      setShowPaymentModal(true);
    } else {
      console.log('campaign offer does not exist');
      navigate('/failed?msg=offer not found');
    }
  }, [campaignOfferId, API.getOffers?.data, from]);

  useEffect(() => {
    if (publisherMetaData.featureFlags?.store_hide_mobile_background_overlay) {
      let viewportMeta = document.querySelector('meta[name="viewport"]');
      if (!viewportMeta) {
        viewportMeta = document.createElement('meta');
        viewportMeta.setAttribute('name', 'viewport');
        document.head.appendChild(viewportMeta);
      }
      viewportMeta.setAttribute(
        'content',
        'initial-scale=1, width=device-width, height=device-height, viewport-fit=cover, user-scalable=no'
      );
    }
  }, [publisherMetaData]);

  useEffect(() => {
    if (isSSE) {
      setShouldReportAndResetOffers(true);
      queryClient.invalidateQueries([EQueryKeys.OFFERS]);
      API.getOffers.refetch();
      setIsSSE(false);
    }
  }, [isSSE]);

  useSSE({
    enabled: publisherMetaData.featureFlags?.store_sse_support,
    onMessage: async (data: string | FreePrizesData, type: string) => {
      switch (type) {
        case ESSEventType.GET_OFFERS_REQUEST:
          setIsSSE(true);
          break;
        case ESSEventType.KEEPALIVE_PING:
          break;
        case ESSEventType.FREE_PRIZE_ORDER_CREATED: {
          const pendingOrders = webStorageUtil.get(ESessionStorageKeys.PENDING_ORDER_ID) || [];
          const alreadyExists = pendingOrders.some((order: FreePrizesData) => {
            if (typeof data !== 'string') return order?.orderId === data?.orderId;
          });
          const updatedOrders = alreadyExists ? pendingOrders : [...pendingOrders, data];
          webStorageUtil.set(ESessionStorageKeys.PENDING_ORDER_ID, updatedOrders);
          break;
        }
        default:
          if (process.env.NODE_ENV !== 'production') {
            console.log('Unknown event type:', type);
          }
          break;
      }
    },
    onError: () => {
      logErrorToCoralogix(
        undefined,
        { EVENT: '[SSE] Falling back to polling' },
        true,
        '[SSE] Falling back to polling'
      );
      customEvents.sendCustomEvent(EEventsType.SSE_POLLING_STARTED, {});
      setInterval(async () => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[SSE] Falling back to polling');
        }
        await API.getOffers.refetch();
      }, POLLING_INTERVAL);
    },
  });

  const getTooltipImagesSrc = async (arrIds: string[]): Promise<IProductResponse[]> => {
    try {
      const res = await API.getProductsAssets(arrIds);
      return res;
    } catch (e) {
      console.log('error', e);
      return [];
    }
  };

  const shouldDisplayCheckout: boolean = useMemo(() => {
    return Boolean((selectedOffer && showPaymentModal) || campaignOfferData || activeOrderId);
  }, [selectedOffer, showPaymentModal, campaignOfferData, activeOrderId]);

  const deepLinks = useMemo(
    () => publisherMetaData?.integration?.deepLinks,
    [publisherMetaData?.integration?.deepLinks]
  );
  const isBackToGameButton =
    publisherMetaData.storeTheme.storeScreen.backToGameButton?.active && isMobile;

  const backToGameButtonPosition = useMemo(
    () => publisherMetaData.storeTheme.storeScreen.backToGameButton?.position || EPosition.LEFT,
    [publisherMetaData.storeTheme.storeScreen.backToGameButton?.position]
  );

  const shouldDisplayTabsInFooter = useMemo(
    () => publisherMetaData.featureFlags?.store_tab_as_footer,
    [publisherMetaData.featureFlags?.store_tab_as_footer]
  );

  const addToHomePageComponent = useMemo(
    () => <AddToHomePage className="w-20 h-20" />,
    [publisherMetaData, customEvents]
  );

  const overrideStore = () => {
    const subDomain = window.prompt('?');
    webStorageUtil.set(ELocalStorageKeys.OVERRIDE_STORE, `${subDomain}.appcharge.com`);
  };

  const mappedTabs = useMemo(() => {
    if (!API.getOffers.data?.tabs) return [];

    return API.getOffers.data?.tabs?.map(({ name, publisherTabId, iconUrl }: any) => ({
      name,
      id: publisherTabId,
      iconUrl,
    }));
  }, [API.getOffers.data]);

  useEffect(() => {
    setIsTabFooterDisplayed(mappedTabs?.length > 0 && shouldDisplayTabsInFooter);

    return () => {
      setIsTabFooterDisplayed(false);
    };
  }, [mappedTabs, shouldDisplayTabsInFooter]);

  const currTab = useMemo(() => {
    if (!API.getOffers.data?.tabs) return undefined;

    const tabId = searchParams.get('tab');

    if (!tabId) return undefined;

    return API.getOffers.data.tabs.find((tab: any) => tab.publisherTabId === tabId)?.tabId;
  }, [searchParams, API.getOffers.data]);

  return (
    <div className="storeWrapper">
      <div
        className="buttonsContainer"
        style={{
          bottom: `${publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.verticalOffset || 0}%`,
          ...(isBackToGameButton && backToGameButtonPosition === EPosition.RIGHT
            ? { left: 'auto', right: '16px' }
            : {}),
        }}
      >
        {addToHomeScreenButtonPosition === EAddToHomeScreenButtonPosition.FLOATING && (
          <div className="fixed right-0 top-0 w-20 h-20 z-50 flex items-center justify-center">
            {addToHomePageComponent}
          </div>
        )}
        {isBackToGameButton && (
          <CustomBackToGameButton
            getOffers={() => API.getOffers.refetch()}
            buttonImage={publisherMetaData.storeTheme.storeScreen.backToGameButton?.buttonImage}
            deepLinks={deepLinks}
          />
        )}
      </div>

      {!(isDesktop || isLandscape) &&
        !publisherMetaData.featureFlags?.store_hide_mobile_background_overlay &&
        backgroundImage && (
          <div
            className={'shop__mobile-img'}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              position: 'fixed',
              zIndex: '-1',
              backgroundSize: 'cover',
              backgroundPosition: 'top center',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              maxHeight: '100vh',
            }}
          ></div>
        )}

      {publisherMetaData.featureFlags.store_deeplinks_disable_store && !from && (
        <Stack alignItems="center" justifyContent="center" width="100%" height="100vh">
          <MessageBox
            title={'This link is not valid'}
            text={'Sorry, this link is no longer valid.'}
            fontFamily={publisherMetaData.storeTheme.general.font}
            borderColor={'#FFFFFF'}
          />
        </Stack>
      )}

      {isProfileEnabled && <IntegrationProfilePicker disabled={true} />}

      {!publisherMetaData.featureFlags.store_deeplinks_disable_store && (
        <HeaderWrapper
          logoPicUrl={API.getOffers?.data?.logo || publisherMetaData.storeTheme.general.logo}
          renderHeaderV2={publisherMetaData.featureFlags.store_header_version_2}
          showHeader={API.getOffers.data !== undefined}
          playerName={webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerName}
          playerId={webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerId}
          openLogoutModal={openLogoutModal}
          logoSize={publisherMetaData.storeTheme.general.logoSize}
          balances={getBalancesData()}
          fontFamily={publisherMetaData.storeTheme.general.font}
          profileFrameImg={API.getOffers?.data?.profileFrameImg || ''}
          playerLevelImg={API.getOffers?.data?.playerLevelImg || ''}
          backgroundOpacity={publisherMetaData.storeTheme.storeScreen?.header?.backgroundOpacity}
          showLogo={publisherMetaData.storeTheme.storeScreen?.header?.showLogo}
          showPlayerName={publisherMetaData.storeTheme.storeScreen?.header?.showPlayerName}
          showPlayerId={publisherMetaData.storeTheme.storeScreen?.header?.showPlayerId}
          isSticky={publisherMetaData.storeTheme.storeScreen?.header?.isSticky}
          textColor={publisherMetaData.storeTheme.storeScreen?.header?.textColor?.colorOne}
          tabs={shouldDisplayTabsInFooter ? [] : mappedTabs}
          actionButton={
            !isDesktop && addToHomeScreenButtonPosition === EAddToHomeScreenButtonPosition.HEADER
              ? addToHomePageComponent
              : undefined
          }
          yourBalanceTranslate={t('balances.balancePrefixText')}
          playerData={webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)}
          publisherMetaData={publisherMetaData}
          publisherMeta={webStorageUtil.get(ELocalStorageKeys.PUBLISHER_META)}
          overrideStore={overrideStore}
          useSearchParams={useSearchParams}
        />
      )}

      <PopupFailScreen />

      <ProductsCollectAnimation
        refetchOffers={API.getOffers.refetch}
        collectedProductsImages={collectedProductsImages}
        setCollectedProductsImages={setCollectedProductsImages}
        shouldDisplayProductsAnimation={shouldDisplayProductsAnimation}
        setIsLoading={setDisplayLoader}
        isDailyBonus={currentPopupDisplayed?.subType === EPopupSubType.DAILY_BONUS}
      />

      <PointsCollectAnimation
        refetchOffers={API.getOffers.refetch}
        collectedPointsImages={collectedPointsImages}
        setCollectedPointsImages={setCollectedPointsImages}
        shouldDisplayPointsAnimation={shouldDisplayProductsAnimation}
        setIsLoading={setDisplayLoader}
      />

      {showConfetti && (
        <Lottie
          animationData={animationData}
          loop={false}
          style={{
            position: 'fixed',
            zIndex: 100000,
            top: '50vh',
            left: '50vw',
            transform: 'translate(-50%, -50%)',
            width: '50%',
            height: '50%',
          }}
          rendererSettings={{
            preserveAspectRatio: 'xMidYMid slice',
          }}
          onComplete={() => {
            setShowConfetti(false);
          }}
          data-testId="storeConfettiAnimation"
        />
      )}

      {!publisherMetaData.featureFlags.store_deeplinks_disable_store &&
        !shouldReportAndResetOffers && (
          <StoreLayout
            useApi={useApi}
            bootData={publisherMetaData}
            activeOrderId={activeOrderId}
            isSSE={isSSE}
            appState={appState}
            popupState={popupState}
            customEvents={customEvents}
            t={t}
            currTab={currTab || undefined}
            shouldDisplayPopups={!shouldDisplayCheckout}
            selectOffer={selectOffer}
            hasNoOffers={hasNoOffers}
            tooltipOpenEventHandler={openTooltipEventHandler}
            tooltipLoadCallback={getTooltipImagesSrc}
            refetchOffers={API.getOffers.refetch}
            sendEvent={() => sendEvent}
          />
        )}
      {showLogoutModal && (
        <LogoutModal
          playerName={webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerName}
          closePopup={closePopup}
          showLogoutModal={showLogoutModal}
          logout={logout}
        />
      )}
      {shouldDisplayCheckout && (
        <CheckoutWrapper
          selectedOffer={
            (campaignOfferData as OfferDataForPublisherEvent) || (selectedOffer as OfferData)
          }
          stubOffer={stubOffer}
          setStubOffer={setStubOffer}
          close={() => closePopup()}
          setLoadingFromCampaign={setLoadingFromCampaign}
          collectId={activeOrderId}
          currencyCode={currencyCode}
        />
      )}
      {failedHashValidation && <HashValidationErrorModal />}
      {isTabFooterDisplayed && (
        <FooterTabs tabs={mappedTabs} useSearchParams={useSearchParams} useApi={useApi} />
      )}
    </div>
  );
};
